/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { BenefitTypeDisplayTextMap, BenefitTypes } from '@strata/constants';
import { DateFormats, formatDate, sortAsc, sortDesc } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { FieldArray, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { post, useGet } from 'services/Http';
import { mapPayerSegment } from 'services/PayerSegmentSupport';
import { getDisabledAfter, getRoutes } from 'services/RouteRuleHelpers';

const DuplicationOption = {
    ALL: 'Duplicate all',
    SUBSET: 'Choose a subset',
};

const RulePlacementSingleSelect = ({
    arrayHelpers,
    payerSegment,
    payerSegmentIndex,
    benefit = undefined,
}) => {
    const [routes, setRoutes] = useState();
    useEffect(() => {
        void (async () => {
            try {
                if (payerSegment.payerId && payerSegment.segmentId && benefit) {
                    setRoutes(
                        (
                            await getRoutes(payerSegment.payerId, payerSegment.segmentId, benefit)
                        ).flatMap((group, i) =>
                            group.data
                                .filter((route) => !route.isPostLiveReferralAssignment)
                                .map((route, j) => ({
                                    value: route.route_rule_id,
                                    name: `After ${route.display_sequence}. ${route.route_name}`,
                                    disabled: getDisabledAfter(i, group.data.length, j),
                                    routeSequence:
                                        route.can_delete === false ? -1 : route.route_sequence, // System routes will have can_delete set to false. If one of these is selected, we need to insert the copied routes at the first sequence position.
                                    beforeContinuity: route.before_continuity,
                                })),
                        ),
                    );
                }
            } catch (err) {
                handleError('An error occurred loading route rules.', err);
            }
        })();
    }, [payerSegment.payerId, payerSegment.segmentId, benefit]);

    return (
        <SuggestSingleSelectFormik
            id={`${arrayHelpers.name}.${payerSegmentIndex}.routeSequence`}
            label="Rule Placement"
            required
            values={routes}
            selectedItem={routes?.find(
                (option) =>
                    !option.disabled &&
                    option.routeSequence === payerSegment.routeSequence &&
                    option.beforeContinuity === payerSegment.beforeContinuity,
            )}
            onChange={(newValue) =>
                arrayHelpers.replace(payerSegmentIndex, {
                    ...payerSegment,
                    routeSequence: newValue?.routeSequence,
                    beforeContinuity: newValue?.beforeContinuity,
                })
            }
        />
    );
};
RulePlacementSingleSelect.propTypes = {
    arrayHelpers: PropTypes.shape({
        name: PropTypes.string.isRequired,
        replace: PropTypes.func.isRequired,
    }).isRequired,
    payerSegment: PropTypes.shape({
        payerId: PropTypes.string,
        segmentId: PropTypes.string,
        routeSequence: PropTypes.number,
        beforeContinuity: PropTypes.bool,
    }).isRequired,
    payerSegmentIndex: PropTypes.number.isRequired,
    benefit: PropTypes.oneOf(Object.values(BenefitTypes)),
};

export const DuplicateRouteRulesDialog = ({
    isOpen,
    onClose,
    payerId = undefined,
    segmentId = undefined,
    segmentName = undefined,
    benefit = undefined,
}) => {
    const customRoutes = useGet(
        isOpen && payerId && segmentId && benefit
            ? `/clients/admin/${payerId}/${segmentId}/route_rules/${benefit}`
            : null,
    );
    const routeOptions = customRoutes
        // Global routes are added to a segment from the Global Routes page, not by duplicating
        ?.filter((route) => !route.is_global)
        .map((route) => ({
            name: route.route_name,
            secondaryData: route.route_description,
            value: route.route_rule_id,
            before_continuity: route.before_continuity,
            route_sequence: route.route_sequence,
        }))
        .sort(
            (a, b) =>
                sortDesc(a.before_continuity?.toString(), b.before_continuity?.toString()) ||
                sortAsc(a.route_sequence?.toString(), b.route_sequence?.toString()),
        );

    const segments = useGet(isOpen ? '/reference/payer_segments' : null);
    const segmentsWithRoutes = useGet(
        isOpen
            ? `/clients/admin/${payerId}/${segmentId}/route_rules/${benefit}/payers/segments`
            : null,
    );
    const payerSegmentOptions = segments
        ?.map(mapPayerSegment)
        .sort(({ name: a }, { name: b }) => sortAsc(a, b));

    const initialValues = useMemo(
        () => ({
            duplicateOption: DuplicationOption.ALL,
            start_date: dayjs().startOf('day').toDate(),
            payerSegments: [{ distinctId: uuidv4() }],
        }),
        [],
    );

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={Yup.object({
                duplicateOption: Yup.string()
                    .oneOf(Object.values(DuplicationOption))
                    .required(validationStrings.requiredField.errorMessage),
                start_date: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                routesToCopy: Yup.array(
                    Yup.object().required(validationStrings.requiredField.errorMessage),
                ).when('duplicateOption', {
                    is: DuplicationOption.SUBSET,
                    then: (schema) =>
                        schema
                            .required(validationStrings.requiredField.errorMessage)
                            .min(1, validationStrings.requiredField.errorMessage),
                }),
                payerSegments: Yup.array()
                    .required(validationStrings.requiredField.errorMessage)
                    .min(1, validationStrings.requiredField.errorMessage)
                    .when('duplicateOption', {
                        is: DuplicationOption.ALL,
                        then: (schema) =>
                            schema.of(
                                Yup.object({
                                    payerId: Yup.string().required(
                                        validationStrings.requiredField.errorMessage,
                                    ),
                                    segmentId: Yup.string().required(
                                        validationStrings.requiredField.errorMessage,
                                    ),
                                }),
                            ),
                        otherwise: (schema) =>
                            schema.of(
                                Yup.object({
                                    payerId: Yup.string().required(
                                        validationStrings.requiredField.errorMessage,
                                    ),
                                    segmentId: Yup.string().required(
                                        validationStrings.requiredField.errorMessage,
                                    ),
                                    routeSequence: Yup.number().required(
                                        validationStrings.requiredField.errorMessage,
                                    ),
                                    beforeContinuity: Yup.bool().required(
                                        validationStrings.requiredField.errorMessage,
                                    ),
                                }),
                            ),
                    }),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    const startDate = formatDate(values.start_date, DateFormats.HYPHEN_DATE);
                    if (values.duplicateOption === DuplicationOption.ALL) {
                        await post(
                            `/clients/admin/${payerId}/${segmentId}/route_rules/${benefit}/duplicate`,
                            {
                                destination_benefit: benefit,
                                start_date: startDate,
                                destination_segments: values.payerSegments.map((segment) => ({
                                    payer_id: segment.payerId,
                                    segment_id: segment.segmentId,
                                })),
                            },
                        );
                    } else {
                        await post(
                            `/clients/admin/${payerId}/${segmentId}/route_rules/${benefit}/duplicate_specific`,
                            {
                                destination_benefit: benefit,
                                start_date: startDate,
                                routes_to_copy: values.routesToCopy.map((route) => route.value),
                                destination_segments: values.payerSegments.map((segment) => ({
                                    payer_id: segment.payerId,
                                    segment_id: segment.segmentId,
                                    before_continuity: segment.beforeContinuity,
                                    sequence_after: segment.routeSequence,
                                })),
                            },
                        );
                    }

                    await SuccessToast({ message: 'Routes successfully duplicated' });

                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Error duplicating routes', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Duplicate Rules"
                    width={600}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Duplicate"
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    dialogContent={
                        <>
                            <RadioButtonGroupFormik
                                name="duplicateOption"
                                label="Rules To Duplicate"
                                required
                                options={Object.values(DuplicationOption)}
                                // Reset form values when changing duplication option
                                onChange={(newVal) =>
                                    formik.setValues({
                                        duplicateOption: newVal,
                                        start_date: formik.values.start_date,
                                        payerSegments: [{ distinctId: uuidv4() }],
                                    })
                                }
                            />
                            <DateInputFormik id="start_date" label="Start Date" required />
                            {formik.values.duplicateOption === DuplicationOption.SUBSET && (
                                <SuggestMultiSelectFormik
                                    id="routesToCopy"
                                    label="Select Route(s)"
                                    required
                                    isLoading={!routeOptions}
                                    values={routeOptions}
                                />
                            )}
                            <div className="margin__bottom--medium">
                                <span>You are duplicating rules from&nbsp;</span>
                                <b>
                                    {segmentName} | {BenefitTypeDisplayTextMap[benefit]}
                                </b>
                                <span>&nbsp;to:</span>
                            </div>
                            <FieldArray name="payerSegments">
                                {(arrayHelpers) => (
                                    <>
                                        {formik.values[arrayHelpers.name].map(
                                            (payerSegment, i, payerSegments) => (
                                                <div
                                                    key={payerSegment.distinctId}
                                                    className="flex__gap--small"
                                                >
                                                    <div style={{ flexGrow: 1 }}>
                                                        <SuggestSingleSelectFormik
                                                            id={`${arrayHelpers.name}.${i}.segmentId`}
                                                            label="Payer Segment"
                                                            required
                                                            helperText={
                                                                formik.values.duplicateOption ===
                                                                DuplicationOption.ALL
                                                                    ? 'Only payer segments without rule configurations are available for selection.'
                                                                    : undefined
                                                            }
                                                            values={payerSegmentOptions
                                                                // Only show segments as an option if they have no routes configured when ALL is selected
                                                                ?.filter(
                                                                    (segment) =>
                                                                        formik.values
                                                                            .duplicateOption ===
                                                                            DuplicationOption.SUBSET ||
                                                                        !segmentsWithRoutes?.includes(
                                                                            segment.segment_id,
                                                                        ),
                                                                )
                                                                .filter(
                                                                    (option) =>
                                                                        !payerSegments
                                                                            .map(
                                                                                (segment) =>
                                                                                    segment.segmentId,
                                                                            )
                                                                            .includes(option.value),
                                                                )}
                                                            selectedItem={payerSegmentOptions?.find(
                                                                (option) =>
                                                                    option.value ===
                                                                    payerSegment.segmentId,
                                                            )}
                                                            onChange={(newValue) =>
                                                                arrayHelpers.replace(i, {
                                                                    distinctId:
                                                                        payerSegment.distinctId,
                                                                    payerId: newValue?.payer_id,
                                                                    segmentId: newValue?.value,
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                    <div
                                                        className="flex__gap--small"
                                                        style={
                                                            formik.values.duplicateOption ===
                                                            DuplicationOption.SUBSET
                                                                ? { flex: '1 1 0' }
                                                                : undefined
                                                        }
                                                    >
                                                        {formik.values.duplicateOption ===
                                                            DuplicationOption.SUBSET && (
                                                            <RulePlacementSingleSelect
                                                                arrayHelpers={arrayHelpers}
                                                                payerSegmentIndex={i}
                                                                payerSegment={payerSegment}
                                                                benefit={benefit}
                                                            />
                                                        )}
                                                        {i > 0 && (
                                                            <Icon
                                                                className="margin__top--large"
                                                                title="Remove segment"
                                                                icon={IconNames.CROSS}
                                                                intent={Icon.Intent.SECONDARY}
                                                                onClick={() =>
                                                                    arrayHelpers.remove(i)
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                        <ActionButtonText
                                            label="Add Another Segment"
                                            leftIcon={IconNames.ADD}
                                            onClick={() =>
                                                arrayHelpers.push({ distinctId: uuidv4() })
                                            }
                                        />
                                    </>
                                )}
                            </FieldArray>
                        </>
                    }
                />
            )}
        </Formik>
    );
};

DuplicateRouteRulesDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    payerId: PropTypes.string,
    segmentId: PropTypes.string,
    segmentName: PropTypes.string,
    benefit: PropTypes.oneOf(Object.values(BenefitTypes)),
};
